<template>
  <div class="h-screen overflow-hidden bg-black select-none relative">
    <ShowMeOverlay />
    <TimersOverlay />
    <AccessibilityRemoteSetupView
      v-if="showAccessibilitySetup"
      @done="onAccessibilitySetupDone"
      @skip="onAccessibilitySetupSkip"
    />
    <WriteSettingsSetupView
      v-if="showWriteSettingsSetup"
      @done="onWriteSettingsSetupDone"
      @skip="onWriteSettingsSetupSkip"
    />
    <transition
      enter-active-class="transition-opacity duration-500"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-500"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <CurrentState v-if="showDebugModal" />
    </transition>

    <!-- Fixed chrome aligned to logical rotation (same model as ShowMe / idle / quota). -->
    <AppViewportChromeShell>
      <transition
        enter-active-class="transition-opacity"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <!-- Fullscreen only on web; on Android (Tauri) app runs kiosk/fullscreen so controls are redundant -->
        <div
          v-if="mouseMoving && showFullscreenControls"
          class="pointer-events-auto absolute top-0 right-0 z-50 m-4 flex items-start space-x-2"
        >
          <button @click="toggleFullScreen">
            <ArrowsPointingOut
              v-if="!isFullScreen"
              class="w-10 h-10 rounded-md transition-colors hover:bg-gray-600"
            />
            <ArrowsPointingIn
              v-else
              class="w-10 h-10 rounded-md transition-colors hover:bg-gray-600"
            />
          </button>
        </div>
      </transition>
      <CampaignUpdateNotification />
      <RegistrationStatusReceivedNotification />
      <transition
        enter-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-300"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="playerUpdateTopOverlay"
          class="pointer-events-auto absolute top-3 left-1/2 z-30 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-blue-500/30 bg-blue-500/20 px-3 py-2 text-sm font-medium text-blue-100 shadow-lg"
        >
          <span v-if="playerUpdateTopOverlay.status === 'available'">Atualização disponível: v{{ playerUpdateTopOverlay.version }}</span>
          <span v-else-if="playerUpdateTopOverlay.status === 'downloading'">A transferir v{{ playerUpdateTopOverlay.version }}… {{ playerUpdateTopOverlay.download != null ? Math.round(playerUpdateTopOverlay.download) : 0 }}%</span>
          <span v-else-if="playerUpdateTopOverlay.status === 'complete'">Atualização pronta: v{{ playerUpdateTopOverlay.version }}</span>
          <span v-else-if="playerUpdateTopOverlay.status === 'complete_pending_restart'">v{{ playerUpdateTopOverlay.version }} transferido — reinicie o player para instalar</span>
          <span v-else-if="playerUpdateTopOverlay.status === 'failed'" class="text-red-200">Erro: {{ playerUpdateTopOverlay.error ?? 'instalação falhou' }}</span>
          <span v-else>v{{ playerUpdateTopOverlay.version }} · {{ playerUpdateTopOverlay.status }}</span>
        </div>
      </transition>
    </AppViewportChromeShell>

    <transition
      :name="playerRootTransitionName"
      @after-enter="onPlayerRootTransitionAfterEnter"
    >
      <div v-if="showQuotaExceededScreen" key="quota" class="player-root-layer">
        <QuotaExceededScreen />
      </div>
      <div
        v-else-if="weeklyAvailabilityPlaybackSuppressed"
        key="availability-off"
        class="player-root-layer bg-black"
        aria-hidden="true"
      />
      <div
        v-else-if="tenant === null || queue.length < 1 || postPlaylistLoadHoldActive"
        key="idle"
        class="player-root-layer"
      >
        <PlayerIdleLayer />
      </div>
      <div
        v-else
        key="playback"
        class="player-root-layer"
        data-player-live-preview-root
        @vue:mounted="notifyLivePreviewPlaybackRootMounted"
      >
        <VBuffer :rotation="viewportCssRotation" />
      </div>
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, toRefs, unref, watch } from 'vue'
import ArrowsPointingIn from '@/assets/svg/arrows-pointing-in.svg'
import ArrowsPointingOut from '@/assets/svg/arrows-pointing-out.svg'
import AccessibilityRemoteSetupView from '@/components/AccessibilityRemoteSetupView.vue'
import AppViewportChromeShell from '@/components/AppViewportChromeShell.vue'
import CampaignUpdateNotification from '@/components/CampaignUpdateNotification.vue'
import CurrentState from '@/components/CurrentState.vue'
import PlayerIdleLayer from '@/components/PlayerIdleLayer.vue'
import QuotaExceededScreen from '@/components/QuotaExceededScreen.vue'
import RegistrationStatusReceivedNotification from '@/components/RegistrationStatusReceivedNotification.vue'
import ShowMeOverlay from '@/components/ShowMeOverlay.vue'
import TimersOverlay from '@/components/TimersOverlay.vue'
import VBuffer from '@/components/VBuffer.vue'
import WriteSettingsSetupView from '@/components/WriteSettingsSetupView.vue'
import { useAccessibilityRemoteSetupGate } from '@/composables/useAccessibilityRemoteSetupGate'
import { useBootSplashUntilPlayback } from '@/composables/useBootSplashUntilPlayback'
import useEventListeners from '@/composables/useEventListeners'
import usePlayer from '@/composables/usePlayer'
import { useWriteSettingsSetupGate } from '@/composables/useWriteSettingsSetupGate'
import { isAndroidPlayStoreDistributionResolved } from '@/helpers/androidDistribution'
import { cssEffectiveScreenRotationFromState } from '@/helpers/cssEffectiveScreenRotation'
import { notifyLivePreviewPlaybackRootMounted } from '@/helpers/playerLivePreviewUpload'
import { isWebFullscreenApiAvailable } from '@/helpers/webFullscreen'
import { getPlatform } from '@/platform'

const { setEventListeners, removeEventListeners } = useEventListeners()

const { state, toggleFullScreen, weeklyAvailabilityPlaybackSuppressed } = usePlayer()
const {
  tenant,
  queue,
  postPlaylistLoadHoldActive,
  showDebugModal,
  mouseMoving,
  isFullScreen,
  update: playerUpdate
} = toRefs(state)

const viewportCssRotation = computed(() =>
  cssEffectiveScreenRotationFromState(state)
)

/**
 * Show fullscreen toggle only on web with a usable Fullscreen API.
 * Android (Tauri) is always kiosk; iOS web often has no API (PLAYER-11) — hide the dead control.
 */
const showFullscreenControls = computed(
  () => getPlatform() === 'web' && isWebFullscreenApiAvailable()
)

/** APK update banner over content is web-only; on Android use CurrentState only (avoids "Atualização pronta" over playback). */
const playerUpdateTopOverlay = computed(() => {
  const u = unref(playerUpdate)
  if (!u || getPlatform() === 'android') { return null }
  return u
})

/** When tenant has exceeded quota, show a dedicated warning screen (no clock / waiting text). */
const showQuotaExceededScreen = computed(() => {
  const t = unref(tenant)
  return t !== null && t.quotaExcess === true
})

useBootSplashUntilPlayback({
  showQuotaExceededScreen,
  weeklyAvailabilityPlaybackSuppressed
})

const {
  showOverlay: showAccessibilitySetup,
  skip: onAccessibilitySetupSkip,
  markDone: onAccessibilitySetupDone
} = useAccessibilityRemoteSetupGate()

// Asked after (never on top of) the Accessibility step.
const {
  showOverlay: showWriteSettingsSetup,
  skip: onWriteSettingsSetupSkip,
  markDone: onWriteSettingsSetupDone
} = useWriteSettingsSetupGate({ blocked: showAccessibilitySetup })

/** Mirrors the idle `v-else-if` branch (excluding quota screen). */
const showIdleRootLayer = computed(() =>
  unref(tenant) === null
  || unref(queue).length < 1
  || unref(postPlaylistLoadHoldActive)
)

/**
 * Leaving idle for playback (first non-empty queue or post-hold reveal) used to run `player-root-crossfade` (~0.5s +
 * ~0.85s), which reads as a long flicker while content is already ready. One-shot instant transition, then restore
 * crossfade for later quota / idle returns.
 */
const playerRootTransitionName = ref<'player-root-crossfade' | 'player-root-instant'>('player-root-crossfade')

function onPlayerRootTransitionAfterEnter(): void {
  playerRootTransitionName.value = 'player-root-crossfade'
}

watch(
  showIdleRootLayer,
  (idle, prevIdle) => {
    if (idle === prevIdle) {
      return
    }
    if (prevIdle === true && idle === false && unref(queue).length >= 1) {
      playerRootTransitionName.value = 'player-root-instant'
    }
  },
  { flush: 'sync' }
)

setEventListeners()
onBeforeUnmount(() => removeEventListeners())

// Fallback: request install-unknown-apps permission when app is mounted (first launch; initPlayer may run before bridge is ready).
onMounted(() => {
  if (getPlatform() !== 'android') { return }
  setTimeout(() => {
    void isAndroidPlayStoreDistributionResolved().then((isPlay) => {
      if (isPlay) { return }
      import('@tauri-apps/api/core').then(({ invoke }) =>
        invoke('plugin:lucimark-android|requestInstallUnknownAppsPermissionIfNeeded').catch(() => {})
      )
    })
  }, 2500)
})
</script>

<style scoped>
.player-root-layer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
</style>

<style>
/*
 * Idle ↔ playback crossfade: slightly asymmetric easing so the incoming view
 * eases in gently (longer) while the outgoing view fades a bit faster.
 */
.player-root-crossfade-leave-active {
  transition: opacity 0.5s cubic-bezier(0.4, 0, 1, 1);
  z-index: 1;
}
.player-root-crossfade-enter-active {
  transition: opacity 0.85s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;
}
.player-root-crossfade-enter-from,
.player-root-crossfade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .player-root-crossfade-leave-active,
  .player-root-crossfade-enter-active {
    transition-duration: 0.01ms;
  }
}

/* One-shot: idle → playback when queue is already populated (see `playerRootTransitionName` in script). */
.player-root-instant-leave-active,
.player-root-instant-enter-active {
  transition: opacity 0.01ms linear;
  z-index: 1;
}
.player-root-instant-enter-from,
.player-root-instant-leave-to {
  opacity: 0;
}
</style>
